/**
 * Export the org's member-profile field configuration to CSV.
 *
 * Produces one row per field, covering both core platform fields and
 * org-defined custom fields. Field-group membership is included so admins
 * can see which repeatable group (if any) owns each field.
 *
 * Called from the Profile Builder admin page.
 */
import {
  CoreField,
  CustomField,
  FieldCategory,
  FieldConfiguration,
  FieldOption,
  VisibilityRules,
} from '@/services/api/members';
import { FieldGroup } from '@/services/api/fieldGroups';
import { exportTableToCSV } from './exportUtils';

const TYPE_DESCRIPTIONS: Record<string, string> = {
  str: 'Text or string values',
  int: 'Whole numbers',
  bool: 'True or false checkbox values',
  date: 'Calendar date',
  datetime: 'Date with time',
  time: 'Time of day only',
  fixed_date: 'Immutable date that cannot be changed after set',
  select: 'Select one value from a predefined list',
  multi_select: 'Choose one or more values from a list',
  checkbox: 'Group of checkboxes from a predefined list',
  radio: 'Select one option from a visible list of choices',
  toggle: 'On/Off switch',
  // Core field types
  text: 'Free text input',
  email: 'Email address',
  phone: 'Phone number',
  number: 'Numeric input',
  ssn: 'Social Security Number',
};

const OPERATOR_LABELS: Record<string, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  is_populated: 'is populated',
  is_empty: 'is empty',
  contains: 'contains',
  in: 'is one of',
};

const DEFAULT_GROUP_LABELS: Record<string, string> = {
  identity: 'Identity',
  demographics: 'Demographics',
  contact: 'Contact',
  address: 'Address',
  insurance: 'Insurance',
  custom: 'Custom',
};

/**
 * Render a predefined value list for the CSV.
 *
 * Option lists reach the client in BOTH shapes — the canonical
 * `[{label, value}]` and a bare `['A', 'B']` string array (some org configs and
 * core-field defaults were seeded that way). Reading `.label` off a bare string
 * yields undefined and throws on `.localeCompare`, so normalize first.
 */
const formatOptions = (
  options?: Array<FieldOption | string> | null,
): string => {
  if (!options || options.length === 0) return '';
  return options
    .map((o) => (typeof o === 'string' ? o : o?.label ?? o?.value ?? ''))
    .filter((label) => label !== '')
    .sort((a, b) => a.localeCompare(b))
    .join('; ');
};

const formatVisibilityRules = (
  rules: VisibilityRules | null | undefined,
  fieldNameToLabel: Map<string, string>,
): string => {
  if (!rules || !rules.conditions || rules.conditions.length === 0) {
    return 'Always visible';
  }
  const joiner = rules.logic === 'OR' ? ' OR ' : ' AND ';
  const parts = rules.conditions.map((c) => {
    const label = fieldNameToLabel.get(c.field) || c.field;
    const op = OPERATOR_LABELS[c.operator] || c.operator;
    const needsValue = c.operator !== 'is_populated' && c.operator !== 'is_empty';
    if (needsValue && c.value) return `${label} ${op} "${c.value}"`;
    return `${label} ${op}`;
  });
  return `Show when ${parts.join(joiner)}`;
};

export interface FieldConfigExportInput {
  coreFields: CoreField[];
  customFields: CustomField[];
  categories: FieldCategory[];
  configurations: FieldConfiguration[];
  fieldGroups: FieldGroup[];
}

export interface ExportRow {
  source: 'Core' | 'Custom';
  internal_name: string;
  display_name: string;
  field_type: string;
  description: string;
  category: string;
  field_group: string;
  required: string;
  hidden: string;
  readonly: string;
  sort_order: string;
  max_length: string;
  default_value: string;
  options: string;
  visibility_rules: string;
  help_text: string;
  placeholder: string;
}

/**
 * Build the export rows, sorted as they appear in the file.
 *
 * Separated from the download so the field-to-column mapping can be asserted
 * directly in tests — the interesting logic is here, and `exportTableToCSV`
 * needs a DOM.
 */
export const buildFieldConfigExportRows = (
  input: FieldConfigExportInput,
): ExportRow[] => {
  const { coreFields, customFields, categories, configurations, fieldGroups } = input;

  // Lookup maps
  const categoryById = new Map<number, FieldCategory>(
    categories.map((c) => [c.id, c]),
  );
  const coreConfigByKey = new Map<string, FieldConfiguration>();
  const customConfigById = new Map<number, FieldConfiguration>();
  configurations.forEach((cfg) => {
    if (cfg.custom_field_id != null) {
      customConfigById.set(cfg.custom_field_id, cfg);
    } else if (cfg.field_key) {
      coreConfigByKey.set(cfg.field_key, cfg);
    } else if (cfg.core_field_id != null) {
      const cf = coreFields.find((f) => f.id === cfg.core_field_id);
      if (cf) coreConfigByKey.set(cf.field_key, cfg);
    }
  });

  // Field-group membership: custom_field_id -> group friendly_name
  const fieldGroupByCustomFieldId = new Map<number, FieldGroup>();
  fieldGroups.forEach((g) => {
    g.children.forEach((child) => {
      fieldGroupByCustomFieldId.set(child.field_id, g);
    });
  });

  // For visibility-rule label substitution (custom-field internal name -> display)
  const fieldNameToLabel = new Map<string, string>();
  customFields.forEach((f) => fieldNameToLabel.set(f.name, f.friendly_name));
  coreFields.forEach((f) => fieldNameToLabel.set(f.field_key, f.default_display_name));

  // ---- Core field rows ----
  const coreRows: ExportRow[] = coreFields.map((f) => {
    const cfg = coreConfigByKey.get(f.field_key);
    const category =
      (cfg?.category_id != null && categoryById.get(cfg.category_id)?.display_name) ||
      DEFAULT_GROUP_LABELS[f.field_group] ||
      f.field_group ||
      '';
    return {
      source: 'Core',
      internal_name: f.field_key,
      display_name: cfg?.display_name || f.default_display_name,
      field_type: f.field_type,
      description: TYPE_DESCRIPTIONS[f.field_type] || '',
      category,
      field_group: '',
      required: cfg?.is_required ? 'Yes' : 'No',
      hidden: cfg?.is_hidden ? 'Yes' : 'No',
      readonly: cfg?.is_readonly ? 'Yes' : 'No',
      sort_order: String(cfg?.sort_order ?? f.sort_order ?? ''),
      max_length: '',
      default_value: '',
      // TRUS-254: enum core fields (gender, race, ethnicity, preferred_language)
      // used to export a blank Options cell, so admins downloading the field
      // configuration never saw the predefined value list. Resolve it the same
      // way the profile renderer and the backend Field Map export do: a per-org
      // config override wins, else inherit the core field's defaults.
      options: formatOptions(cfg?.field_options ?? f.default_field_options),
      visibility_rules: 'Always visible',
      help_text: cfg?.help_text || '',
      placeholder: cfg?.placeholder_text || '',
    };
  });

  // ---- Custom field rows ----
  const customRows: ExportRow[] = customFields.map((f) => {
    const cfg = customConfigById.get(f.id);
    const group = fieldGroupByCustomFieldId.get(f.id);
    const category =
      (cfg?.category_id != null && categoryById.get(cfg.category_id)?.display_name) ||
      f.category ||
      '';
    return {
      source: 'Custom',
      internal_name: f.name,
      display_name: cfg?.display_name || f.friendly_name,
      field_type: f.type,
      description: TYPE_DESCRIPTIONS[f.type] || '',
      category,
      field_group: group?.friendly_name || '',
      required: f.mandatory || cfg?.is_required ? 'Yes' : 'No',
      hidden: f.hidden || cfg?.is_hidden ? 'Yes' : 'No',
      readonly: cfg?.is_readonly ? 'Yes' : 'No',
      sort_order: String(cfg?.sort_order ?? ''),
      max_length: f.type === 'str' ? String(f.str_len ?? '') : '',
      default_value: f.default_value ?? '',
      // Same precedence as core rows: an org that set the predefined list on
      // the per-cbo configuration layer rather than on the field itself was
      // getting a blank Options cell (TRUS-254).
      options: formatOptions(cfg?.field_options ?? f.field_options),
      visibility_rules: formatVisibilityRules(f.visibility_rules, fieldNameToLabel),
      help_text: cfg?.help_text || '',
      placeholder: cfg?.placeholder_text || '',
    };
  });

  // Sort: by field_group (empty groups last), then category, then display name
  const sortKey = (r: ExportRow) =>
    `${r.field_group ? `0_${r.field_group}` : '1'}|${r.category}|${r.display_name}`;
  return [...coreRows, ...customRows].sort((a, b) =>
    sortKey(a).localeCompare(sortKey(b)),
  );
};

/**
 * Build the row set and download the CSV.
 */
export const exportFieldConfigToCSV = (
  input: FieldConfigExportInput,
  filenamePrefix = 'field_configuration',
): void => {
  const allRows = buildFieldConfigExportRows(input);

  const headers = [
    'Source',
    'Internal Name',
    'Display Name',
    'Field Type',
    'Description',
    'Category',
    'Field Group',
    'Required',
    'Hidden',
    'Read-Only',
    'Sort Order',
    'Max Length',
    'Default Value',
    'Options',
    'Visibility Rules',
    'Help Text',
    'Placeholder',
  ];

  const rows = allRows.map((r) => [
    r.source,
    r.internal_name,
    r.display_name,
    r.field_type,
    r.description,
    r.category,
    r.field_group,
    r.required,
    r.hidden,
    r.readonly,
    r.sort_order,
    r.max_length,
    r.default_value,
    r.options,
    r.visibility_rules,
    r.help_text,
    r.placeholder,
  ]);

  exportTableToCSV(headers, rows, filenamePrefix);
};
